import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Input, Select, Space, Tag, Avatar, App, Button, Modal, Form, Tooltip, Flex, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, put, del } from '../../api/client';

/**
 * 管理端客户行（后端 AdminCustomersController.toAdminView 的唯一下发形状）。
 *
 * 不含 `customerTypeName` / `tagCount` / `remark`：这三者挂在 UserCustomer 上
 * （每个绑定用户各存一份），一个客户被 N 个用户绑定时就有 N 套值，全局视角下无定义。
 * `idCard` 恒为掩码值，只读展示用，**不可回写**（后端会拦成 400）。
 */
interface AdminCustomerRow {
  id: string;
  fullName: string | null;
  phone: string | null;
  backupPhone: string | null;
  email: string | null;
  companyName: string | null;
  location: string | null;
  address: string | null;
  idCard: string | null;
  emergencyContact: string | null;
  emergencyPhone: string | null;
  introduction: string | null;
  avatar: string | null;
  status: string | null;
  stopReason: string | null;
  isUser: boolean;
  /** 有多少用户把此人加为客户 */
  boundUserCount: number;
  orderCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/** 分页响应壳，与 /admin/users 一致：{ items, total, page, pageSize } */
interface CustomersResponse {
  items: AdminCustomerRow[];
  total: number;
  page: number;
  pageSize: number;
}

const statusTag: Record<string, { color: string; label: string }> = {
  '激活': { color: 'green', label: '激活' },
  '停用': { color: 'gold', label: '停用' },
  '禁用': { color: 'red', label: '禁用' },
  '已合并': { color: 'default', label: '已合并' },
};

/** 后端 DELETE /admin/customers/:id 的三道拒绝闸门，逐条对应控制器里的守护；返回空串即可删。 */
function deleteBlockReason(record: AdminCustomerRow): string {
  if (record.status === '已合并') return '该客户已被合并为次档，不可再次删除';
  if (record.isUser) return '注册用户的主档不可在此删除，需由本人走注销流程或联系管理员处理';
  if (record.boundUserCount > 0) {
    return `仍有 ${record.boundUserCount} 名用户绑定了该客户，需全部解绑后才能删除`;
  }
  return '';
}

export default function CustomersPage() {
  const { message: apiMessage } = App.useApp();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingCustomer, setEditingCustomer] = useState<AdminCustomerRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  /** 存整行而非只存 id：确认弹窗要按名下的订单数说清「会连带删掉什么」。 */
  const [deletingRow, setDeletingRow] = useState<AdminCustomerRow | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-customers', page, pageSize, keyword, statusFilter],
    queryFn: () =>
      get<CustomersResponse>('/admin/customers', {
        page,
        pageSize,
        ...(keyword ? { keyword } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    meta: { errorMessage: '加载客户列表失败' },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<AdminCustomerRow> & { id: string }) => {
      return put(`/admin/customers/${data.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setShowEditModal(false);
      setEditingCustomer(null);
      apiMessage.success('客户信息已更新');
    },
    onError: (error: Error) => {
      apiMessage.error(`更新失败: ${error.message}`);
    },
  });

  /** 后端如实回传的处置结果：档案被引用时只删单、留档，界面不能一律说「客户已删除」。 */
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return del<{ archiveDeleted: boolean; ordersDeleted: number }>(`/admin/customers/${id}`);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setShowDeleteModal(false);
      setDeletingRow(null);
      if (result?.archiveDeleted === false) {
        apiMessage.success(
          `已删除该客户名下的 ${result.ordersDeleted} 笔订单，客户档案因系统内仍被引用而保留`,
        );
      } else {
        apiMessage.success('客户已删除');
      }
    },
    onError: (error: Error) => {
      apiMessage.error(`删除失败: ${error.message}`);
    },
  });

  const handleEdit = (record: AdminCustomerRow) => {
    setEditingCustomer(record);
    setShowEditModal(true);
  };

  const handleDelete = (record: AdminCustomerRow) => {
    setDeletingRow(record);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingRow) {
      deleteMutation.mutate(deletingRow.id);
    }
  };

  const columns = [
    {
      title: '客户',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      render: (value: string | null, record: AdminCustomerRow) => (
        <Flex align="center" gap={12}>
          <Avatar
            src={record.avatar || undefined}
            icon={<UserAddOutlined />}
            size={40}
            style={record.avatar ? undefined : { backgroundColor: '#dbeafe', color: '#1d4ed8' }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{value || '未命名'}</div>
            {record.companyName && (
              <div className="admin-muted admin-ellipsis" style={{ maxWidth: 150 }}>
                {record.companyName}
              </div>
            )}
          </div>
        </Flex>
      ),
    },
    {
      title: '联系方式',
      dataIndex: 'phone',
      key: 'contact',
      width: 180,
      render: (value: string | null, record: AdminCustomerRow) => (
        <Flex vertical gap={2}>
          {value && <div>{value}</div>}
          {record.backupPhone && <div className="admin-muted">备用: {record.backupPhone}</div>}
          {record.email && <div className="admin-muted admin-ellipsis" style={{ maxWidth: 160 }}>{record.email}</div>}
        </Flex>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string | null) => {
        const info = statusTag[value || ''] || { color: 'default', label: value || '未知' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: '身份',
      dataIndex: 'isUser',
      key: 'isUser',
      width: 100,
      render: (value: boolean) => (
        value ? <Tag color="green">已注册</Tag> : <Tag color="default">非注册</Tag>
      ),
    },
    {
      title: '绑定用户',
      dataIndex: 'boundUserCount',
      key: 'boundUserCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => <span>{value}</span>,
    },
    {
      title: '订单数',
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 80,
      align: 'center' as const,
      render: (value: number) => <span>{value}</span>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (value: string | null) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: AdminCustomerRow) => {
        // 后端一定会拒的三种情况（次档 / 注册主档 / 仍有绑定）就地禁用并说明原因，
        // 不让人点下去只收到一句红色报错。
        const blockReason = deleteBlockReason(record);
        return (
          <Space size="middle">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.status === '已合并'}
            >
              编辑
            </Button>
            {blockReason ? (
              <Tooltip title={blockReason}>
                <span>
                  <Button type="link" size="small" icon={<DeleteOutlined />} disabled danger>
                    删除
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                type="link"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                danger
              >
                删除
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Flex vertical gap={16}>
      {/* 头部卡片 */}
      <Card>
        <Flex justify="space-between" align="center" gap={16} wrap="wrap">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>客户管理</Typography.Title>
            <Typography.Text type="secondary">管理系统内所有客户信息，支持搜索、筛选、编辑和删除</Typography.Text>
          </div>
          <Flex align="center" gap={8}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => apiMessage.info('请在 Web 端客户页面新建客户')}
            >
              新建客户
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              刷新
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* 筛选栏 */}
      <Card>
        <Flex gap={12} wrap="wrap">
          <Input
            placeholder="搜索姓名、手机号、邮箱..."
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select
            placeholder="状态筛选"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            allowClear
          >
            <Select.Option value="">全部状态</Select.Option>
            <Select.Option value="激活">激活</Select.Option>
            <Select.Option value="停用">停用</Select.Option>
            <Select.Option value="禁用">禁用</Select.Option>
            <Select.Option value="已合并">已合并</Select.Option>
          </Select>
        </Flex>
      </Card>

      {/* 表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 编辑弹窗 */}
      {editingCustomer && showEditModal && (
        <CustomerEditModal
          customer={editingCustomer}
          onSave={(data) => updateMutation.mutate(data)}
          onClose={() => {
            setShowEditModal(false);
            setEditingCustomer(null);
          }}
          saving={updateMutation.isPending}
        />
      )}

      {/* 删除确认弹窗 */}
      <Modal
        title="删除确认"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeletingRow(null);
        }}
        okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
      >
        <p>确定要删除客户「{deletingRow?.fullName || deletingRow?.phone || deletingRow?.email || '该档案'}」吗？此操作不可撤销。</p>
        {/* 旧文案「有关联用户或订单则拒绝」已不成立：能点到这里说明无绑定，剩下的分支是删单留档，
            而孤立客户名下的本人订单会一并物理删除，必须提前说清。 */}
        <p className="admin-muted" style={{ marginTop: 8 }}>
          该客户已无任何用户绑定。
          {deletingRow && deletingRow.orderCount > 0
            ? `其名下 ${deletingRow.orderCount} 笔由你创建的订单（含付款凭证）会一并删除；`
            : ''}
          若档案在他人账本或审计记录中仍被引用，则只删订单、档案保留。
        </p>
      </Modal>
    </Flex>
  );
}

/* ---- 编辑弹窗 ---- */
function CustomerEditModal({
  customer,
  onSave,
  onClose,
  saving,
}: {
  customer: AdminCustomerRow;
  onSave: (data: Partial<AdminCustomerRow> & { id: string }) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const [form] = Form.useForm();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSave({ id: customer.id, ...values });
    });
  };

  return (
    <Modal
      title="编辑客户信息"
      open={true}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={saving}
      width={600}
    >
      <Form form={form} layout="vertical" initialValues={customer}>
        <div>
          <Form.Item
            name="fullName"
            label="客户姓名"
            rules={[{ required: true, message: '请输入客户姓名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="手机号码">
            <Input />
          </Form.Item>
          <Form.Item name="backupPhone" label="备用号码">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="电子邮箱">
            <Input />
          </Form.Item>
          <Form.Item name="companyName" label="公司/单位">
            <Input />
          </Form.Item>
          <Form.Item name="location" label="所在城市">
            <Input />
          </Form.Item>
          <Form.Item name="emergencyContact" label="紧急联系人">
            <Input />
          </Form.Item>
          <Form.Item name="emergencyPhone" label="紧急联系人电话">
            <Input />
          </Form.Item>
        </div>
        <Form.Item name="introduction" label="备注信息">
          <Input.TextArea rows={3} />
        </Form.Item>

        {/*
          刻意不提供「身份证号」与「状态」两个入口：
          - 下发的 idCard 恒为掩码串，当普通输入框预填再保存会把掩码写进 id_card_enc
            （静默的 PII 损坏，且 id_card_hash 随之失效、之后再也撞不中这条档案）。
            证件号是身份锚点，修正入口属实证/人工审核流程。
          - 状态只能走 PATCH /admin/users/:id/status：那条路径强制「禁用必须填写原因」
            并在解除时清空历史原因，从 PUT 改状态会绕过这套管制留痕。
        */}
        <Flex vertical gap={4} className="admin-muted" style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          <div>
            档案状态：
            <Tag color={(statusTag[customer.status || ''] || { color: 'default' }).color}>
              {(statusTag[customer.status || ''] || { label: customer.status || '未知' }).label}
            </Tag>
            {customer.stopReason && <span>原因：{customer.stopReason}</span>}
          </div>
          <div>身份证号：{customer.idCard || '—'}（只读，如需修正请走实证/人工审核流程）</div>
          <div>状态变更请前往「用户管理」页，停用/禁用需填写原因。</div>
        </Flex>
      </Form>
    </Modal>
  );
}